import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShoppingBasket, ArrowRight, Loader2, PackageX, Plus, Minus, Trash2, Search, Users, Wifi } from "lucide-react";

export default function SharedBasket() {
  const { token } = useParams();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const updatedAtRef = useRef("");
  const pendingRef = useRef(false);
  const saveTimer = useRef();
  const searchTimer = useRef();

  useEffect(() => {
    api.get(`/shared/${token}`)
      .then((r) => { setName(r.data.name); setItems(r.data.items); updatedAtRef.current = r.data.updated_at || ""; })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Poll for remote changes (real-time collaboration)
  useEffect(() => {
    if (error) return;
    const id = setInterval(async () => {
      if (pendingRef.current) return;
      try {
        const { data } = await api.get(`/shared/${token}`);
        if (data.updated_at && data.updated_at > updatedAtRef.current) {
          updatedAtRef.current = data.updated_at;
          setItems(data.items);
          setName(data.name);
        }
      } catch (e) {}
    }, 4000);
    return () => clearInterval(id);
  }, [token, error]);

  const persist = useCallback((nextItems) => {
    pendingRef.current = true;
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/shared/${token}`, {
          items: nextItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        });
        updatedAtRef.current = data.updated_at || "";
      } catch (e) { toast.error("Synchronisation impossible"); }
      finally { pendingRef.current = false; setSaving(false); }
    }, 600);
  }, [token]);

  const mutate = (next) => { setItems(next); persist(next); };

  const setQty = (id, delta) =>
    mutate(items.map((x) => (x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + delta) } : x)));
  const removeItem = (id) => mutate(items.filter((x) => x.product_id !== id));
  const addItem = (p) => {
    const found = items.find((x) => x.product_id === p.id);
    const next = found
      ? items.map((x) => (x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x))
      : [...items, { product_id: p.id, quantity: 1, name: p.name, brand: p.brand, unit: p.unit }];
    mutate(next);
    toast.success(`${p.name} ajouté`);
  };

  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!query) { setResults([]); return; }
    searchTimer.current = setTimeout(() => {
      api.get("/products/search", { params: { q: query, limit: 8 } }).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [query]);

  const loadIntoComparator = () => {
    localStorage.setItem("pmc_basket", JSON.stringify(
      items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, name: i.name, brand: i.brand, unit: i.unit }))
    ));
    toast.success("Panier chargé dans le comparateur");
    nav("/comparateur");
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        {loading && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {!loading && error && (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <PackageX className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <h1 className="font-display text-xl font-800 text-secondary">Panier introuvable</h1>
            <p className="mt-2 text-sm text-muted-foreground">Ce lien de partage n'est plus valide.</p>
            <Link to="/comparateur"><Button className="mt-5 rounded-full" data-testid="shared-go-comparateur">Aller au comparateur</Button></Link>
          </div>
        )}

        {!loading && !error && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-card p-8" data-testid="shared-basket">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShoppingBasket className="h-6 w-6" /></div>
                <div>
                  <h1 className="font-display text-2xl font-800 text-secondary">{name}</h1>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" /> Panier collaboratif · {items.length} article(s)
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="gap-1 border-primary/30 text-primary" data-testid="collab-status">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wifi className="h-3 w-3" />}
                {saving ? "Sync..." : "En direct"}
              </Badge>
            </div>

            <div className="relative mt-6">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ajouter un produit..." className="pl-9" data-testid="shared-search-input" />
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                  {results.map((p) => (
                    <button key={p.id} onClick={() => { addItem(p); setQuery(""); setResults([]); }} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-muted" data-testid={`shared-add-${p.id}`}>
                      <span className="text-secondary">{p.name}</span>
                      <Plus className="h-4 w-4 text-primary" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <AnimatePresence>
                {items.map((it) => (
                  <motion.div key={it.product_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    className="flex items-center gap-2 rounded-lg border border-border p-3" data-testid={`shared-item-${it.product_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-secondary">{it.name}</p>
                      <p className="text-xs text-muted-foreground">{it.brand} · {it.unit}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, -1)} data-testid={`shared-minus-${it.product_id}`}><Minus className="h-3 w-3" /></Button>
                      <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, 1)} data-testid={`shared-plus-${it.product_id}`}><Plus className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(it.product_id)} data-testid={`shared-remove-${it.product_id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Panier vide — ajoutez des produits</p>}
            </div>

            <Button onClick={loadIntoComparator} className="mt-6 w-full rounded-full" disabled={items.length === 0} data-testid="shared-load-btn">
              Comparer ce panier <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Partagez ce lien : toute personne peut modifier ce panier, les changements apparaissent en direct.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
